import { Check } from "lucide-react";
import { SIGN_IN_URL, SIGN_UP_URL } from "../lib/site";
import { Wordmark } from "./Mark";

/** Positioning (PRD §20): one line against each thing people use today. */
const FIT: [string, string][] = [
	["Prediction markets", "They tell you the odds. ModelCurve adds the why, and what it does to you."],
	["Forecasting sites", "Forecasts that connect: to each other, and to your own numbers."],
	["Spreadsheets", "Your plan, with the world’s risks priced in."],
	["AI chat", "Your AI builds the model, and the model stays, ready to argue with next week."],
];

export function Fit() {
	return (
		<section className="mx-auto max-w-[1240px] px-5 pb-24 md:px-8 lg:pb-32">
			<div className="grid gap-x-16 gap-y-10 border-t border-line pt-16 lg:grid-cols-12">
				<h2 className="text-[clamp(1.9rem,3vw,2.5rem)] leading-[1.05] tracking-[-0.03em] lg:col-span-4">Where it fits.</h2>
				<dl className="grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:col-span-8">
					{FIT.map(([what, line]) => (
						<div key={what}>
							<dt className="text-sm text-quiet">{what}</dt>
							<dd className="mt-1.5 text-lg leading-snug font-medium tracking-[-0.01em] text-ink">{line}</dd>
						</div>
					))}
				</dl>
			</div>
		</section>
	);
}

type Plan = { name: string; price: string; per?: string; blurb: string; items: string[]; cta?: { href: string; label: string } };
const PLANS: Plan[] = [
	{
		name: "Free",
		price: "$0",
		blurb: "The whole product as it stands today.",
		items: [
			"Events, metrics, links and scenarios",
			"Drag editing, the graph, the table and the comparison",
			"Claude over MCP, and the command-line tool",
			"An activity log with one-click undo",
		],
		cta: { href: SIGN_UP_URL, label: "Start free" },
	},
	{
		name: "Pro",
		price: "Coming later",
		blurb: "For people who model for a living.",
		items: ["Up to 500 private events and metrics", "Larger simulations", "Alerts when a number moves", "Higher limits for Claude"],
	},
	{
		name: "Teams",
		price: "Coming later",
		blurb: "One worldview for the whole team.",
		items: ["Owner, editor and viewer roles", "Data from Sheets, accounting and CRM", "Single sign-on and an audit log", "Exports for board packs"],
	},
];

export function Pricing() {
	return (
		<section id="pricing" className="mx-auto max-w-[1240px] px-5 pb-24 md:px-8 lg:pb-32">
			<div className="grid gap-x-16 gap-y-6 lg:grid-cols-12">
				<h2 className="text-[clamp(2.25rem,4.4vw,3.5rem)] leading-[1.02] tracking-[-0.035em] lg:col-span-6">Free until it earns its keep.</h2>
				<p className="lg:col-span-5 lg:col-start-8 lg:pt-2">
					Everything that exists today is free. Paid plans come when there is more to pay for, and the first model you build with Claude will never be metered.
				</p>
			</div>
			<div className="mt-14 grid overflow-hidden rounded-md border border-line bg-white md:grid-cols-3">
				{PLANS.map((p, i) => (
					<div key={p.name} className={`flex flex-col p-6 sm:p-8 ${i > 0 ? "border-t border-line md:border-t-0 md:border-l" : ""}`}>
						<h3 className="text-base font-semibold">{p.name}</h3>
						<div
							className={`mt-3 flex h-10 items-end leading-none font-semibold tracking-[-0.03em] ${p.cta ? "text-[40px] text-ink" : "text-[22px] text-quiet"}`}
						>
							{p.price}
						</div>
						<p className="mt-3 text-sm text-quiet">{p.blurb}</p>
						<ul className="mt-6 space-y-2.5 text-sm">
							{p.items.map((it) => (
								<li key={it} className="flex gap-2.5">
									<Check size={16} className={`mt-[3px] shrink-0 ${p.cta ? "text-peri" : "text-[#b7b2ba]"}`} aria-hidden />
									<span className={p.cta ? "text-eggplant" : "text-quiet"}>{it}</span>
								</li>
							))}
						</ul>
						<div className="mt-auto pt-8">
							{p.cta ? (
								<a href={p.cta.href} className="btn btn-primary w-full">
									{p.cta.label}
								</a>
							) : (
								<span className="flex min-h-[44px] items-center justify-center rounded-sm border border-dashed border-line text-sm text-quiet">Not yet</span>
							)}
						</div>
					</div>
				))}
			</div>
		</section>
	);
}

export function Close() {
	return (
		<section className="on-night relative overflow-hidden">
			<div className="relative mx-auto grid max-w-[1240px] items-center gap-12 px-5 py-24 md:px-8 lg:grid-cols-12 lg:py-32">
				<div className="lg:col-span-7">
					<h2 className="text-[clamp(2.5rem,5.4vw,4.5rem)] leading-[0.98] tracking-[-0.04em]">Forecasts are opinions. Make yours specific.</h2>
					<p className="mt-6 max-w-[32rem] text-lg text-night-quiet">Start with one worry and a date. Claude can do the typing.</p>
					<div className="mt-9 flex flex-wrap gap-3">
						<a href={SIGN_UP_URL} className="btn btn-primary">
							Start free
						</a>
						<a href={SIGN_IN_URL} className="btn btn-secondary">
							Sign in
						</a>
					</div>
				</div>
				<div className="hidden justify-center lg:col-span-5 lg:flex" aria-hidden>
					{/* The app-icon tile, as brand/logo/tile-dark.svg draws it. */}
					<img src="/tile-dark.svg" alt="" width={280} height={280} className="h-[280px] w-[280px]" />
				</div>
			</div>
		</section>
	);
}

export function Footer() {
	return (
		<footer className="on-night border-t border-night-line">
			<div className="mx-auto flex max-w-[1240px] flex-col gap-8 px-5 py-12 text-sm md:flex-row md:items-start md:justify-between md:px-8">
				<div>
					<Wordmark className="text-white" />
					<p className="mt-4 max-w-[26rem] text-night-quiet">Forecasts are opinions and models, not facts or advice.</p>
				</div>
				<nav aria-label="Footer" className="flex flex-wrap gap-x-7 gap-y-2 text-night-quiet">
					<a href="#links" className="hover:text-white">
						How it works
					</a>
					<a href="#claude" className="hover:text-white">
						Claude
					</a>
					<a href="#pricing" className="hover:text-white">
						Pricing
					</a>
					<a href={SIGN_IN_URL} className="hover:text-white">
						Sign in
					</a>
					<a href={SIGN_UP_URL} className="hover:text-white">
						Start free
					</a>
				</nav>
			</div>
			<div className="mx-auto max-w-[1240px] px-5 pb-10 text-xs text-night-quiet/70 md:px-8">© 2026 ModelCurve</div>
		</footer>
	);
}
